import { LINE_LEVELS, type Line, type LineLevel } from '../../../shared/types';

/**
 * The variations inside one opening, grouped by level.
 *
 * This replaced a wrapping row of pills above the board. That worked at four
 * variations and broke badly at eighteen: the names run to forty-odd characters
 * ("Pianissimo: Black hunts the bishop with ...Na5"), so the row wrapped to six
 * lines and pushed the board off the screen. A vertical list is the shape that
 * actually scales here, because it grows in the direction there is room to grow.
 *
 * Grouping by level is not decoration. Variations arrive as a ladder, and the
 * heading counts ("INTERMEDIATE 6") are how you see that raising your level
 * would hand you six more lines before you commit to it.
 *
 * Once an opening is in your repertoire each row also carries a checkbox, which
 * is what makes an arbitrary subset possible: the level ladder is a fast way to
 * pick a sensible set, not the only way to pick one. Wanting three specific
 * master variations without everything beneath them is a completely reasonable
 * thing to want, and it used to be unexpressible.
 *
 * One component serves both layouts. On desktop it is a sticky column beside
 * the board; on a phone the same list collapses behind a row naming the current
 * variation, so the board stays visible without scrolling. The collapse is
 * driven by CSS rather than a JS media query, so there is no layout flash on
 * first paint and nothing to keep in sync with the breakpoints.
 */

const LEVEL_LABEL: Record<LineLevel, string> = {
  opening: 'The opening',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  master: 'Master',
};

export default function VariationList({
  lines,
  activeId,
  onSelect,
  /**
   * Ids currently scheduled, or null when the opening is not in the repertoire
   * (in which case no checkboxes are shown at all — there is nothing to select
   * into yet).
   */
  selectedIds,
  onToggleSelected,
  open,
  onToggle,
}: {
  lines: Line[];
  activeId: string | null;
  onSelect: (lineId: string) => void;
  selectedIds?: string[] | null;
  onToggleSelected?: (lineId: string) => void;
  open: boolean;
  onToggle: () => void;
}) {
  const groups = LINE_LEVELS.map((level) => ({
    level,
    items: lines.filter((line) => line.level === level),
  })).filter((group) => group.items.length > 0);

  const activePosition = lines.findIndex((l) => l.id === activeId);
  const active = activePosition >= 0 ? lines[activePosition] : lines[0];
  const selecting = Array.isArray(selectedIds);

  return (
    <div className={`variations${open ? ' variations--open' : ''}`}>
      <button
        type="button"
        className="variations-toggle"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="variation-list"
      >
        <span className="variations-toggle-text">
          <span className="variations-toggle-name">{active?.name ?? 'Variations'}</span>
          <span className="variations-toggle-meta">
            {active
              ? `${active.level} · ${Math.max(activePosition, 0) + 1} of ${lines.length}`
              : `${lines.length} variations`}
          </span>
        </span>
        <span className="variations-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      <div className="variations-list" id="variation-list" role="tablist" aria-label="Variations">
        <h2 className="variations-head">
          Variations
          <span className="variations-total">
            {selecting ? `${selectedIds!.length} of ${lines.length}` : lines.length}
          </span>
        </h2>
        {selecting && (
          <p className="variations-note muted">Ticked variations are scheduled for practice.</p>
        )}
        {groups.map((group) => (
          <div key={group.level} className="variations-group">
            <h3 className="variations-group-head">
              <span className={`level-dot level-dot--${group.level}`} aria-hidden="true" />
              {LEVEL_LABEL[group.level]}
              <span className="variations-group-count">{group.items.length}</span>
            </h3>
            {group.items.map((line) => {
              const scheduled = selecting && selectedIds!.includes(line.id);
              return (
                <div
                  key={line.id}
                  className={[
                    'variation-row',
                    line.id === active?.id ? 'variation-row--active' : '',
                    selecting && !scheduled ? 'variation-row--unscheduled' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {selecting && (
                    <input
                      type="checkbox"
                      className="variation-check"
                      checked={scheduled}
                      onChange={() => onToggleSelected?.(line.id)}
                      aria-label={`Practice ${line.name}`}
                      title={
                        scheduled
                          ? 'Scheduled — untick to stop practicing it'
                          : 'Not scheduled — tick to add it'
                      }
                    />
                  )}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={line.id === active?.id}
                    className="variation"
                    onClick={() => onSelect(line.id)}
                    title={line.name}
                  >
                    <span className="variation-name">{line.name}</span>
                    <span className="variation-plies">{Math.ceil(line.moves.length / 2)}</span>
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
